import { useEffect, useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { Dialog, Button, Text, Alert, Form } from '@/ui'
import {
  ADMIN_CREATE_USER_ACTION_ITEM_KEY,
  adminCreateUserActionItem,
} from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import {
  fetchAllTodos,
  selectTodos,
} from '@/features/admin/action-items/allActionItems.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

interface UserTodoCreationDialogProps {
  open: boolean
  close: () => void
  userId: number
}

export const UserTodoCreationDialog = ({
  open,
  close,
  userId,
}: UserTodoCreationDialogProps) => {
  const dispatch = useAppDispatch()
  const error = useReselector(getFetchError, ADMIN_CREATE_USER_ACTION_ITEM_KEY)

  const allTodos = useReselector(selectTodos)

  const todoOptions = useMemo(
    () =>
      allTodos.map((todo) => ({
        text: todo.description,
        value: todo.id,
      })),
    [allTodos]
  )

  useEffect(() => {
    fetchAllTodos()(dispatch)
  }, [dispatch])

  const validationSchema = yup.object({
    actionItemId: yup.number().required('You must select a todo.'),
  })

  const formik = useFormik({
    initialValues: {
      actionItemId: 0,
    },
    validationSchema,
    onSubmit: async (actionItemId: { actionItemId: number }) => {
      const res = await adminCreateUserActionItem(
        userId,
        actionItemId
      )(dispatch)
      if (res) {
        close()
      }
    },
  })

  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Content>
        <div>
          <Text variant={TypographyStyles.H1}>
            Assign a todo for User to complete
          </Text>
          <Text variant={TypographyStyles.BODY_MD} className="my-4">
            This allows you to add a todo for the user to complete. This will
            show up on a user&#39;s Dashboard.
          </Text>
          {error && <Alert variant="error">{error.message as string}</Alert>}
          <FormikProvider value={formik}>
            <div className="space-y-4">
              <Text variant={TypographyStyles.H3}>Select Todo</Text>
              <Form.Select name="actionItemId" options={todoOptions} />
            </div>
            <Dialog.Footer className="mt-4">
              <Dialog.Close asChild>
                <Button variant="secondary" onClick={close}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button variant="primary" onClick={() => formik.handleSubmit()}>
                Create
              </Button>
            </Dialog.Footer>
          </FormikProvider>
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
